import { ActionButton } from '@components/button/types'
import { useDialog } from '@globals/dialog/useDialogStore'
import { colors } from '@globals/themes/defaultTheme'
import { tabletQuery } from '@globals/themes/themeConstants'
import { useOnboardingFabVisible } from '@globals/useOnboardingFabStore'
import usePreviousPath from '@globals/usePreviousPath'
import { useTrialBannerHeight } from '@globals/useTrialBannerStore'
import HelpOutlineOutlinedIcon from '@mui/icons-material/HelpOutlineOutlined'
import AppBar from '@mui/material/AppBar'
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import Stack from '@mui/material/Stack'
import { SxProps } from '@mui/material/styles'
import Typography from '@mui/material/Typography'
import useMediaQuery from '@mui/material/useMediaQuery'
import { DRAWER_WIDTH } from '@nav/Nav.constants'
import RightDrawer from '@shared/dialog/RightDrawer'
import { T, useT } from '@transifex/react'
import { filter } from 'lodash/fp'
import React, { ReactNode, createContext, useEffect, useState } from 'react'
import ActionButtons, { ACTION_BUTTONS_OFFSET } from './ActionButtons'
import BackArrow from './BackArrow'
import MoreActionsMenu from './MoreActionsMenu'

export const DIALOG_KEY = 'helpDialog'

const appBarStyle: SxProps = {
  width: '100%',
  boxShadow: 'none',
  background: colors.blueGradient,
  color: colors.white,
  borderBottom: `1px solid ${colors.grayExtraLight}`,
  borderRadius: 0,
  borderLeft: 'none',
  borderRight: 'none',
  left: DRAWER_WIDTH,
  [tabletQuery]: {
    background: colors.white,
    left: 0,
    textAlign: 'center',
  },
}

const constrainedWidthLayoutStyle: SxProps = {
  margin: 'auto',
  overflow: 'visible',
}

const helpButtonStyle: SxProps = {
  backgroundColor: 'transparent',
  '&:hover': {
    backgroundColor: 'transparent',
  },
  '&:focus': {
    backgroundColor: 'transparent',
  },
}

const moreActionsMenuMobileStyle: SxProps = {
  [tabletQuery]: {
    position: 'fixed',
    top: 4,
    right: 8,
  },
}

const titleStyle: SxProps = {
  overflow: 'hidden',
  whiteSpace: 'nowrap',
  textOverflow: 'ellipsis',
}

export const StandardPageLayoutContext = createContext<StandardPageLayoutContextProps | undefined>(
  undefined,
)

type StandardPageLayoutProps = {
  elementId: string
  maxWidth: 600 | 700 | 900 | 1080 | 1440 | '100%'
  title?: ReactNode
  children: React.ReactNode
}

type SetBackToPath = {
  (path: -1, fallback: string): void
  (path: string): void
}

type StandardPageLayoutContextProps = {
  setPageTitle: (title: ReactNode) => void
  setBackToPath: SetBackToPath
  setChips: (options: ReactNode[]) => void
  setActionButtons: (actions: ActionButton[]) => void
  setHelpContent: (value: { content: ReactNode; title?: ReactNode }) => void
  openHelpDialog: () => void
  setTabPanel: (tabPanel: ReactNode) => void
  appBarHeight: number
  setSubTabPanel: (subTabPanel: ReactNode) => void
  setMobileMultiFilter: (node: ReactNode) => void
}

const StandardPageLayout: React.FC<StandardPageLayoutProps> = ({
  elementId,
  title,
  maxWidth,
  children,
}) => {
  const t = useT()
  const { generatePreviousPath } = usePreviousPath()
  const { isOnboardingFabVisible } = useOnboardingFabVisible()
  const { trialBannerHeight } = useTrialBannerHeight()
  const topBarRef = React.createRef<HTMLDivElement>()
  const isTablet = useMediaQuery(tabletQuery)
  const [appBarHeight, setAppBarHeight] = useState<number>(50)
  const [pageTitle, setPageTitle] = useState<ReactNode>(title)
  const [backToPath, setBackToPath] = useState<string | number>()
  const [chips, setChips] = useState<ReactNode[]>()
  const [actionButtons, setActionButtons] = useState<ActionButton[]>([])
  const [helpContent, setHelpContent] = useState<{
    content: ReactNode
    title?: ReactNode
  }>()
  const [tabPanel, setTabPanel] = useState<ReactNode>()
  const [subTabPanel, setSubTabPanel] = useState<ReactNode>()
  const [mobileMultiFilter, setMobileMultiFilter] = useState<ReactNode>()

  useEffect(() => {
    if (topBarRef.current) {
      setAppBarHeight(topBarRef.current.clientHeight)
    }
  }, [topBarRef])

  useEffect(() => {
    if (!isTablet) {
      setMobileMultiFilter(null)
    }
  }, [isTablet])

  const HELP_DIALOG_KEY = `${elementId}${DIALOG_KEY}`
  const { openDialog: openHelpDialog } = useDialog(HELP_DIALOG_KEY)

  const moreActionButtons = actionButtons?.filter(action => action.variant !== 'primary')

  if (isTablet && !!helpContent) {
    moreActionButtons.push({
      key: 'help',
      name: t('Help'),
      variant: 'other',
      onClick: () => openHelpDialog(),
    })
  }

  const handleSetBackToPath = (path: string | -1, fallback?: string) => {
    if (path === -1) setBackToPath(generatePreviousPath(fallback as string))
    else setBackToPath(path)
  }

  const contextValue: StandardPageLayoutContextProps = {
    setPageTitle,
    setBackToPath: handleSetBackToPath,
    setChips,
    setActionButtons,
    setHelpContent,
    openHelpDialog,
    setTabPanel,
    appBarHeight,
    setSubTabPanel,
    setMobileMultiFilter,
  }

  return (
    <>
      <AppBar
        ref={topBarRef}
        position="fixed"
        color="default"
        elevation={0}
        sx={{
          ...appBarStyle,
          top: isTablet ? 0 : trialBannerHeight,
        }}>
        <Stack
          direction={isTablet ? 'column' : 'row'}
          alignItems={isTablet ? 'flex-start' : 'center'}
          spacing={1}
          mt={isTablet ? 0 : 5.5}
          pt={2}
          pb={isTablet ? 1 : 2}
          px={isTablet ? 2 : 3}
          width={isTablet ? '100%' : `calc(100% - ${DRAWER_WIDTH}px)`}>
          <Stack
            direction="row"
            alignItems="center"
            spacing={1}
            minWidth={0}
            pl={!!backToPath && isTablet ? '40px' : 0}
            width={isTablet ? '100%' : 'auto'}
            maxWidth="calc(100% - 40px)">
            {!!backToPath && <BackArrow to={backToPath} />}
            <Typography
              variant={isTablet ? 'h2' : 'h1'}
              sx={{ ...titleStyle, flex: 1, minWidth: 0 }}>
              {pageTitle}
            </Typography>
          </Stack>
          {!!chips && (
            <Stack useFlexGap sx={{ flexDirection: { xs: 'column', sm: 'row' } }} spacing={1}>
              {chips.map((chip, index) => (
                <Box key={`standardPageChip-${index}`}>{chip}</Box>
              ))}
            </Stack>
          )}
          <Box sx={{ flexGrow: 1 }} />
          {(!!actionButtons || !!mobileMultiFilter) && (
            <ActionButtons
              actions={
                isTablet
                  ? filter({ variant: 'primary' }, actionButtons)
                  : actionButtons?.filter(action => action.variant !== 'other')
              }
            />
          )}
          {!!helpContent && !isTablet && (
            <Button
              id="btnHelp"
              aria-label={t('Help information')}
              sx={helpButtonStyle}
              onClick={() => openHelpDialog()}>
              <HelpOutlineOutlinedIcon />
            </Button>
          )}
          {((!!moreActionButtons && (moreActionButtons?.length ?? 0) > 0) ||
            !!mobileMultiFilter) && (
            <Box sx={moreActionsMenuMobileStyle}>
              <Stack direction="row" spacing={1}>
                {mobileMultiFilter}
                <MoreActionsMenu
                  actions={
                    isTablet ? moreActionButtons : filter({ variant: 'other' }, moreActionButtons)
                  }
                />
              </Stack>
            </Box>
          )}
        </Stack>
        {!!tabPanel && <Box sx={{ px: 3 }}>{tabPanel}</Box>}
      </AppBar>
      <Box
        sx={{
          ...constrainedWidthLayoutStyle,
          pt: `${appBarHeight + trialBannerHeight + 20}px`,
          [tabletQuery]: {
            pt: `${appBarHeight + 16}px`,
            pb: !!actionButtons.length || isOnboardingFabVisible ? `${ACTION_BUTTONS_OFFSET}px` : 3,
          },
        }}
        maxWidth={maxWidth}>
        <StandardPageLayoutContext.Provider value={contextValue}>
          {!!subTabPanel && <Box sx={{ mb: 3, px: isTablet ? 2 : 0 }}>{subTabPanel}</Box>}
          <Box sx={{ px: isTablet ? 2 : 0 }}>{children}</Box>
          {!!helpContent && (
            <RightDrawer
              dialogKey={HELP_DIALOG_KEY}
              title={!!helpContent.title ? helpContent.title : <T _str="Help" />}>
              {helpContent.content}
            </RightDrawer>
          )}
        </StandardPageLayoutContext.Provider>
      </Box>
    </>
  )
}

export default StandardPageLayout
